// The engine's input (spec E2, "The engine"): a project resolved from the editor's timeline, each
// clip with its file's URL and in the order it draws, so the preview, Download and the Save page
// all feed the same thing to the scene. Pure: no React, no DOM.
import type { EditAspect as AspectRatio, EffectKind, TransitionKind } from "../../../api/generated/model";
import { sourceKey, type SourceMedia } from "../media";
import type { Animation } from "../animations";
import { captionFaces, type CaptionAnimation, type CaptionStyle } from "../captions/caption-style";
import type { CaptionBox, CaptionWord } from "../captions/words";
import type { EffectValue } from "../effect-filters";
import type { Face } from "../fonts";
import type { Look } from "../look";
import type { SpeedPoint } from "../speed-curve";
import { shownText, type TextStyle } from "../text-style";
import { junctionsOf, totalMs, type ClipSource, type Timeline, type VolumePoint } from "../timeline";

export interface Size {
  width: number;
  height: number;
}

/** A picture on the frame: a video read from `inMs`, or a still (`inMs` 0), with its look. */
export interface Layer {
  clipId: string;
  kind: "video" | "image";
  url: string;
  startMs: number;
  endMs: number;
  inMs: number;
  /** How fast it reads its file (spec clip-speed): 1 for a still; with a curve, its mean. */
  speed: number;
  /** Its speed curve, in its file's time (spec clip-speed-curves): none for a still. */
  speedPoints: readonly SpeedPoint[];
  /**
   * The span it shows in: its own, widened by the transitions at its cuts (#240), where it holds
   * its first frame before its start and its last after its end.
   */
  shownFromMs: number;
  shownToMs: number;
  /** Live's Transform, Flip, Opacity and Radius, in live's pixels (#241). */
  look: Look;
  /** Live's Animations (#266), played over `startMs` to `endMs`. */
  animations: readonly Animation[];
  /** Its track's place from the bottom: 0 for the bottom track (#265, effects stack by it). */
  depth: number;
}

/** Live's transition (#240): `kind` from one picture to the next, centred on their cut. */
export interface TransitionLayer {
  id: string;
  kind: TransitionKind;
  fromId: string;
  toId: string;
  startMs: number;
  endMs: number;
}

/** A sound in the mix: an audio clip, or a video's own sound. */
export interface Sound {
  clipId: string;
  url: string;
  startMs: number;
  endMs: number;
  inMs: number;
  /** How fast it reads its file (spec clip-speed), its pitch kept; with a curve, its mean. */
  speed: number;
  /** Its speed curve, in its file's time (spec clip-speed-curves). */
  speedPoints: readonly SpeedPoint[];
  /** 0 to 2: live's Volume, and up to 200 % (spec volume-boost). */
  volume: number;
  /** Its volume keyframes, in its file's time (spec volume-keyframes). */
  volumePoints: readonly VolumePoint[];
  fadeInMs: number;
  fadeOutMs: number;
}

/** A text over every picture, as live's Parameters set it (#241 part 2). */
export interface TextLayer {
  clipId: string;
  text: string;
  style: TextStyle;
  startMs: number;
  endMs: number;
  /** Live's Animations (#266). */
  animations: readonly Animation[];
  depth: number;
}

/**
 * Live's effect (#265): an adjustment layer over every picture and text of the tracks below its
 * own (`depth`), from `startMs` to `endMs`.
 */
export interface EffectLayer {
  clipId: string;
  effect: EffectKind;
  /** Live's Configuration of a Pixi filter: what the clip changed (#265 part 2). */
  values: Readonly<Record<string, EffectValue>>;
  startMs: number;
  endMs: number;
  depth: number;
}

/** Live's caption (#239): its words over every picture, in the project's caption style. */
export interface CaptionLayer {
  clipId: string;
  words: readonly CaptionWord[];
  box: CaptionBox;
  /** Its own uniform scale and turn about its drawn lines' centre (#560), live's caption's own. */
  scale: number;
  rotation: number;
  startMs: number;
  endMs: number;
  depth: number;
}

export interface EngineProject {
  size: Size;
  /** The frame's pixels to one of live's (`LIVE_FRAME`): 2/3 at 720p, 7/8 for 21:9's 1920 by 823. */
  unit: number;
  fps: number;
  durationMs: number;
  /** Bottom first: each draws over the ones before it, so the top track's are in front. */
  layers: readonly Layer[];
  /** Every sound heard: the unmuted sound clips and videos, on unmuted tracks. */
  sounds: readonly Sound[];
  /** Bottom text track first, so the top track's text draws over the others. */
  texts: readonly TextLayer[];
  /** Each draws its two pictures, mixed, in their place among the layers. */
  transitions: readonly TransitionLayer[];
  /** Bottom first; none hidden. */
  effects: readonly EffectLayer[];
  /** Live's captions (#239), none hidden, and their one look and animation (#264). */
  captions: readonly CaptionLayer[];
  captionLook: { style: CaptionStyle; animation: CaptionAnimation | null };
}

/** The server's render and the preview: 720 pixels on the short side, at 30 frames a second. */
export const PREVIEW_SHORT_SIDE = 720;
/**
 * Live's frame of each ratio, its canvas sizes (live's ratio menu, `canvas-panel.tsx`): a
 * picture's look, a text's style and a caption's place are in its pixels. Five are 1080 on their
 * short side; 21:9 is live's 1920 by 823.
 */
export const LIVE_FRAME: Readonly<Record<AspectRatio, Size>> = {
  "16:9": { width: 1920, height: 1080 },
  "9:16": { width: 1080, height: 1920 },
  "1:1": { width: 1080, height: 1080 },
  "4:5": { width: 1080, height: 1350 },
  "4:3": { width: 1440, height: 1080 },
  "21:9": { width: 1920, height: 823 },
};

/** Live's frame of a ratio, in its pixels. */
export function liveFrame(aspect: AspectRatio): Size {
  return LIVE_FRAME[aspect];
}
export const PREVIEW_FPS = 30;

const SHAPE: Record<AspectRatio, Size> = {
  "16:9": { width: 16, height: 9 },
  "9:16": { width: 9, height: 16 },
  "1:1": { width: 1, height: 1 },
  "4:5": { width: 4, height: 5 },
  "4:3": { width: 4, height: 3 },
  "21:9": { width: 21, height: 9 },
};

/** The frame of a ratio with `short` pixels on its short side, both sides even (H.264's rule). */
export function frameOf(aspect: AspectRatio, short = PREVIEW_SHORT_SIDE): Size {
  const shape = SHAPE[aspect];
  const even = (value: number) => 2 * Math.round(value / 2);
  const unit = short / Math.min(shape.width, shape.height);
  return { width: even(shape.width * unit), height: even(shape.height * unit) };
}

export interface ResolveOptions {
  /** Pixels on the frame's short side. */
  short?: number;
  fps?: number;
}

/**
 * The project the engine draws and mixes. A clip whose file is not known yet (its media still
 * loading, or gone) is left out of the pictures and the mix; the length stays the timeline's.
 */
export function resolveProject(
  timeline: Timeline,
  aspect: AspectRatio,
  media: ReadonlyMap<string, SourceMedia>,
  options: ResolveOptions = {},
): EngineProject {
  const urlOf = (source: ClipSource) => media.get(sourceKey(source))?.url ?? null;
  const trackAt = new Map(timeline.tracks.map((track, at) => [track.id, at]));
  const depthOf = (trackId: string) => timeline.tracks.length - 1 - (trackAt.get(trackId) ?? 0);
  const mutedTracks = new Set(timeline.tracks.filter((track) => track.muted).map((t) => t.id));
  // Bottom track first; along a track, earliest first.
  const ordered = [...timeline.clips].sort(
    (one, other) =>
      (trackAt.get(other.trackId) ?? 0) - (trackAt.get(one.trackId) ?? 0) ||
      one.startMs - other.startMs,
  );
  // Each transition centred on its cut; a picture shows over the transitions at its cuts.
  const transitions: TransitionLayer[] = junctionsOf(timeline).flatMap(({ transition, atMs }) => {
    if (transition === null) return [];
    const { id, kind, fromId, toId, durationMs } = transition;
    return [{ id, kind, fromId, toId, startMs: atMs - durationMs / 2, endMs: atMs + durationMs / 2 }];
  });
  const shownFrom = new Map(transitions.map((each) => [each.toId, each.startMs]));
  const shownTo = new Map(transitions.map((each) => [each.fromId, each.endMs]));
  const layers: Layer[] = [];
  const sounds: Sound[] = [];
  const texts: TextLayer[] = [];
  const effects: EffectLayer[] = [];
  const captions: CaptionLayer[] = [];
  for (const clip of ordered) {
    const { startMs, endMs } = clip;
    const depth = depthOf(clip.trackId);
    if (clip.kind === "text") {
      // Live's Hide: a hidden text is not drawn.
      if (clip.hidden) continue;
      texts.push({
        clipId: clip.id,
        text: clip.text,
        style: clip.style,
        startMs,
        endMs,
        animations: clip.animations,
        depth,
      });
      continue;
    }
    if (clip.kind === "caption") {
      // Live's Hide: a hidden caption is not drawn.
      if (!clip.hidden) {
        captions.push({
          clipId: clip.id,
          words: clip.words,
          box: clip.box,
          scale: clip.scale,
          rotation: clip.rotation,
          startMs,
          endMs,
          depth,
        });
      }
      continue;
    }
    if (clip.kind === "effect") {
      // A hidden effect is not applied.
      if (!clip.hidden) effects.push({ clipId: clip.id, effect: clip.effect, values: clip.values, startMs, endMs, depth });
      continue;
    }
    const url = urlOf(clip.source);
    if (url === null) continue;
    // Live's Hide: a hidden picture is not drawn; a hidden video is still heard.
    if (clip.kind !== "audio" && !clip.hidden) {
      const inMs = clip.kind === "video" ? clip.inMs : 0;
      const speed = clip.kind === "video" ? clip.speed : 1;
      const speedPoints = clip.kind === "video" ? clip.speedPoints : [];
      const { box, rotation, flipX, flipY, opacity, radius, stroke, shadow, chromaKey } = clip;
      const look = { box, rotation, flipX, flipY, opacity, radius, stroke, shadow, chromaKey };
      const shownFromMs = Math.min(startMs, shownFrom.get(clip.id) ?? startMs);
      const shownToMs = Math.max(endMs, shownTo.get(clip.id) ?? endMs);
      layers.push({
        clipId: clip.id,
        kind: clip.kind,
        url,
        startMs,
        endMs,
        inMs,
        speed,
        speedPoints,
        shownFromMs,
        shownToMs,
        look,
        animations: clip.animations,
        depth,
      });
    }
    if (clip.kind === "image") continue;
    // A muted video or sound (live's Mute) is not heard, nor anything on a muted track.
    const heard = !clip.muted && !mutedTracks.has(clip.trackId);
    if (heard) {
      const { volume, volumePoints, fadeInMs, fadeOutMs, inMs, speed, speedPoints } = clip;
      sounds.push({ clipId: clip.id, url, startMs, endMs, inMs, speed, speedPoints, volume, volumePoints, fadeInMs, fadeOutMs });
    }
  }
  const size = frameOf(aspect, options.short);
  return {
    size,
    unit: size.width / liveFrame(aspect).width,
    fps: options.fps ?? PREVIEW_FPS,
    durationMs: totalMs(timeline),
    layers,
    sounds,
    texts,
    transitions,
    effects,
    captions,
    captionLook: { style: timeline.captions.style, animation: timeline.captions.animation },
  };
}

/** The faces a project's texts are drawn in, each with the characters it draws there. */
export function facesOf(project: EngineProject): Face[] {
  const faces = new Map<string, Face>();
  for (const { text, style } of project.texts) {
    const key = `${style.font}/${style.weight}/${style.italic}`;
    const shown = shownText(text, style.textCase);
    const known = faces.get(key);
    faces.set(key, {
      family: style.font,
      weight: style.weight,
      italic: style.italic,
      text: (known?.text ?? "") + shown,
    });
  }
  // A caption's words in its face, an emphasised word's in its tag's (#239, #264).
  const words = project.captions.flatMap((caption) => caption.words);
  for (const face of captionFaces(words, project.captionLook.style)) {
    const key = `${face.family}/${face.weight}/${face.italic}`;
    const known = faces.get(key);
    faces.set(key, { ...face, text: (known?.text ?? "") + face.text });
  }
  return [...faces.values()];
}
